import { useMemo, useCallback, useState, useEffect } from 'react';
import { Product } from '../../../types/productType';
import { getMaturityStatus, MaturityStatus } from '../constants/maturityWindows';
import { RegionAllocation } from './useCellarStats';

const STORAGE_KEY = 'cellar_dismissed_insights';
const SESSION_SHOWN_KEY = 'cellar_session_shown_insights';

interface WineInsight {
  maturity?: MaturityStatus;
  demandSignal?: string;
  performanceNote?: string;
  hasBidAboveCost?: boolean;
  hasBidAboveMarket?: boolean;
}

export interface ConcentrationWarning {
  type: 'region' | 'single-wine' | 'vintage';
  subject: string;
  percentage: number;
  severity: 'info' | 'warning' | 'elevated';
}

interface CellarInsights {
  // Maturity insights
  winesApproachingPeak: Product[];
  winesAtPeak: Product[];
  winesPastPeak: Product[];

  // Market demand insights
  winesWithActiveBids: Product[];
  winesWithBidsAboveCost: Product[];
  winesWithBidsAboveMarket: Product[];

  // Concentration warnings
  concentrationWarnings: ConcentrationWarning[];

  // Performance insights
  strongPerformers: Product[];
  winesWithEarlyGains: Product[];

  // Insight for individual wine
  getWineInsight: (item: Product) => WineInsight;

  // User preferences
  dismissedInsights: string[];
  dismissInsight: (insightId: string) => void;
  isInsightDismissed: (insightId: string) => boolean;

  // Session tracking
  markInsightShown: (insightId: string) => void;
  hasInsightBeenShown: (insightId: string) => boolean;
}

/**
 * Calculates whether a wine has strong P&L performance.
 * Strong performance: P&L > +30% AND held > 2 years
 */
const isStrongPerformer = (item: Product): boolean => {
  if (!item.totalCost || item.totalCost <= 0) return false;

  const pnlPercentage = ((item.profitAndLoss || 0) / item.totalCost) * 100;
  if (pnlPercentage <= 30) return false;

  // Check holding duration (more than 2 years)
  if (!item.dealDate) return false;
  const dealDate = new Date(item.dealDate);
  const now = new Date();
  const yearsHeld = (now.getTime() - dealDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);

  return yearsHeld >= 2;
};

/**
 * Calculates whether a wine has early gains.
 * Early gains: Held < 1 year, P&L > +20%
 */
const hasEarlyGains = (item: Product): boolean => {
  if (!item.totalCost || item.totalCost <= 0) return false;

  const pnlPercentage = ((item.profitAndLoss || 0) / item.totalCost) * 100;
  if (pnlPercentage <= 20) return false;

  if (!item.dealDate) return false;
  const dealDate = new Date(item.dealDate);
  const now = new Date();
  const yearsHeld = (now.getTime() - dealDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);

  return yearsHeld < 1;
};

/**
 * Gets the performance note for a wine based on its P&L and holding duration.
 */
const getPerformanceNote = (item: Product): string | undefined => {
  if (!item.dealDate) return undefined;

  const dealDate = new Date(item.dealDate);
  const now = new Date();
  const yearsHeld = (now.getTime() - dealDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  const dealYear = dealDate.getFullYear();

  if (isStrongPerformer(item)) {
    return `performingWellSince:${dealYear}`;
  }

  if (hasEarlyGains(item)) {
    return 'strongEarlyPerformance';
  }

  // Long-term hold with modest returns
  if (!item.totalCost || item.totalCost <= 0) return undefined;
  const pnlPercentage = ((item.profitAndLoss || 0) / item.totalCost) * 100;

  if (yearsHeld > 5 && pnlPercentage < 10 && pnlPercentage > -10) {
    return 'longTermHolding';
  }

  return undefined;
};

/**
 * Checks if a wine has an active bid above the per-unit cost.
 */
const hasBidAboveCost = (item: Product): boolean => {
  const bidPrice = item.highestBid?.priceGbp ?? item.highestBidPrice ?? null;
  if (bidPrice === null) return false;

  const costPerUnit = item.costPerUnit || item.totalCost / item.qty || 0;
  return bidPrice > costPerUnit;
};

/**
 * Checks if a wine has a bid at or above market value.
 * Strong buyer interest: highestBid >= 95% of valuePerUnit
 */
const hasBidAboveMarket = (item: Product): boolean => {
  const bidPrice = item.highestBid?.priceGbp ?? item.highestBidPrice ?? null;
  if (bidPrice === null) return false;

  const marketValue = item.valuePerUnit || 0;
  if (marketValue <= 0) return false;

  return bidPrice >= marketValue * 0.95;
};

/**
 * Gets demand signal message for a wine.
 */
const getDemandSignal = (item: Product): string | undefined => {
  const bidPrice = item.highestBid?.priceGbp ?? item.highestBidPrice ?? null;

  if (bidPrice === null) return undefined;

  if (hasBidAboveMarket(item)) {
    return `activeBuyerInterest:${bidPrice}`;
  }

  if (hasBidAboveCost(item)) {
    return `bidAboveCost:${bidPrice}`;
  }

  return undefined;
};

/**
 * Hook that calculates insights for the wine cellar.
 */
export const useCellarInsights = (
  data: Product[],
  regionAllocations: RegionAllocation[],
  totalValue: number,
): CellarInsights => {
  // Load dismissed insights from localStorage
  const [dismissedInsights, setDismissedInsights] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Session-based shown tracking (reset on page refresh)
  const [sessionShown, setSessionShown] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_SHOWN_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Persist dismissed insights to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissedInsights));
    } catch {
      // Ignore storage errors
    }
  }, [dismissedInsights]);

  // Persist session shown to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_SHOWN_KEY, JSON.stringify(Array.from(sessionShown)));
    } catch {
      // Ignore storage errors
    }
  }, [sessionShown]);

  const dismissInsight = useCallback((insightId: string) => {
    setDismissedInsights((prev) => [...prev, insightId]);
  }, []);

  const isInsightDismissed = useCallback(
    (insightId: string) => dismissedInsights.includes(insightId),
    [dismissedInsights],
  );

  const markInsightShown = useCallback((insightId: string) => {
    setSessionShown((prev) => new Set([...prev, insightId]));
  }, []);

  const hasInsightBeenShown = useCallback((insightId: string) => sessionShown.has(insightId), [sessionShown]);

  // Calculate all insights
  const insights = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        winesApproachingPeak: [],
        winesAtPeak: [],
        winesPastPeak: [],
        winesWithActiveBids: [],
        winesWithBidsAboveCost: [],
        winesWithBidsAboveMarket: [],
        concentrationWarnings: [],
        strongPerformers: [],
        winesWithEarlyGains: [],
      };
    }

    // Maturity insights
    const winesApproachingPeak: Product[] = [];
    const winesAtPeak: Product[] = [];
    const winesPastPeak: Product[] = [];

    // Market demand insights
    const winesWithActiveBids: Product[] = [];
    const winesWithBidsAboveCost: Product[] = [];
    const winesWithBidsAboveMarket: Product[] = [];

    // Performance insights
    const strongPerformers: Product[] = [];
    const winesWithEarlyGains: Product[] = [];

    data.forEach((item) => {
      // Maturity
      const maturityStatus = getMaturityStatus(item.vintage, item.cultWinesAllocationRegion);
      if (maturityStatus === 'approaching') winesApproachingPeak.push(item);
      if (maturityStatus === 'peak') winesAtPeak.push(item);
      if (maturityStatus === 'past') winesPastPeak.push(item);

      // Market demand
      const bidPrice = item.highestBid?.priceGbp ?? item.highestBidPrice ?? null;
      if (bidPrice !== null) {
        winesWithActiveBids.push(item);
        if (hasBidAboveCost(item)) winesWithBidsAboveCost.push(item);
        if (hasBidAboveMarket(item)) winesWithBidsAboveMarket.push(item);
      }

      // Performance
      if (isStrongPerformer(item)) strongPerformers.push(item);
      if (hasEarlyGains(item)) winesWithEarlyGains.push(item);
    });

    // Concentration warnings
    const concentrationWarnings: ConcentrationWarning[] = [];

    // Regional concentration
    regionAllocations.forEach((region) => {
      if (region.percentage >= 70) {
        concentrationWarnings.push({
          type: 'region',
          subject: region.name,
          percentage: region.percentage,
          severity: 'elevated',
        });
      } else if (region.percentage >= 50) {
        concentrationWarnings.push({
          type: 'region',
          subject: region.name,
          percentage: region.percentage,
          severity: 'warning',
        });
      }
    });

    // Single-wine concentration (if any wine is >20% of total value)
    if (totalValue > 0) {
      data.forEach((item) => {
        const itemPercentage = ((item.totalValue || 0) / totalValue) * 100;
        if (itemPercentage >= 20) {
          concentrationWarnings.push({
            type: 'single-wine',
            subject: `${item.wineName} ${item.vintage || ''}`.trim(),
            percentage: itemPercentage,
            severity: 'warning',
          });
        }
      });
    }

    return {
      winesApproachingPeak,
      winesAtPeak,
      winesPastPeak,
      winesWithActiveBids,
      winesWithBidsAboveCost,
      winesWithBidsAboveMarket,
      concentrationWarnings,
      strongPerformers,
      winesWithEarlyGains,
    };
  }, [data, regionAllocations, totalValue]);

  // Function to get insights for a single wine
  const getWineInsight = useCallback((item: Product): WineInsight => {
    return {
      maturity: getMaturityStatus(item.vintage, item.cultWinesAllocationRegion),
      demandSignal: getDemandSignal(item),
      performanceNote: getPerformanceNote(item),
      hasBidAboveCost: hasBidAboveCost(item),
      hasBidAboveMarket: hasBidAboveMarket(item),
    };
  }, []);

  return {
    ...insights,
    getWineInsight,
    dismissedInsights,
    dismissInsight,
    isInsightDismissed,
    markInsightShown,
    hasInsightBeenShown,
  };
};
